import 'react-responsive-carousel/lib/styles/carousel.min.css'
import { Carousel } from 'react-responsive-carousel'
import { forwardRef, useCallback, useEffect, useRef, useState } from 'react'
import { ChevronLeftIcon, ChevronRightIcon } from '@icons'
import { Button, Link, Avatar } from '@platform/ui-kit'
import { useWindowSize, WEBSITE_BREAKPOINTS } from '@platform/utils'
import { m, LazyMotion, domAnimation } from 'framer-motion'

import CircleButton from '../../circle-button'
import { BRANDS_INFO } from '../../../utils/constants'
import ShareThisAuthorAvatar from '../../../assets/pngs/company-testimonial-share-this.png'
import HapanaAuthorAvatar from '../../../assets/pngs/company-testimonial-hapana.png'
import TNLAuthorAvatar from '../../../assets/pngs/company-testimonial-tnl.png'
import HighnoteAuthorAvatar from '../../../assets/pngs/company-testimonial-highnote.png'
import LightyearAuthorAvatar from '../../../assets/pngs/company-testimonial-lightyear.png'

import styles from './styles.module.scss'

const FIRST_ITEM_INDEX = 0

const STEP_ITEMS = [
  {
    ...BRANDS_INFO['shareThis'],
    externalHref: BRANDS_INFO['shareThis'].href,
    text: "“We tried Olby to scale our design and engineering teams, and the results couldn't be better. What stood out was the energy, the communication, and how much they cared about doing their best. The team synergy was there continuously. That's why we never left them.”",
    name: 'Imran Sheikh',
    position: 'Director of Product',
    href: '/blog/case-studies/share-this',
    avatar: ShareThisAuthorAvatar,
  },
  {
    ...BRANDS_INFO['hapana'],
    externalHref: BRANDS_INFO['hapana'].href,
    text: '“Great people. Highly skilled. Passionate. We are very lucky to have met Olby and the entire team.”',
    name: 'Jarron Aizen',
    position: 'CEO & Founder',
    href: '/blog/case-studies/hapana',
    avatar: HapanaAuthorAvatar,
  },
  {
    ...BRANDS_INFO['theNewLifeChallenge'],
    externalHref: BRANDS_INFO['theNewLifeChallenge'].href,
    text: '“I enjoy the communication with the Olby team. The work is exactly what is proposed and expected. All costs are projected honestly, and the agile approach makes the most sense for growing businesses and startups”',
    name: 'Sebastien Braxton',
    position: 'CEO',
    avatar: TNLAuthorAvatar,
  },
  {
    ...BRANDS_INFO['highnote'],
    externalHref: BRANDS_INFO['highnote'].href,
    text: '“Well-run team that focuses on taking care of the client in the end!”',
    name: 'Mark Choey',
    position: 'CEO',
    avatar: HighnoteAuthorAvatar,
  },
  {
    ...BRANDS_INFO['lightyear'],
    externalHref: BRANDS_INFO['lightyear'].href,
    text: '“Olby talent have been an awesome development team to work with. They are very responsive, collaborative, and focused on making our product the best it can be.”',
    name: 'Hunter Conrad',
    position: 'CPO',
    avatar: LightyearAuthorAvatar,
  },
]

const NUMBER_OF_ITEM = STEP_ITEMS.length

// Magic number
const WIDTH_ADJUSTMENT = 16

// Animations
const ANIMATION_PROPS = {
  variants: {
    start: { y: '50%', opacity: 0 },
    end: {
      y: '0%',
      opacity: 1,
      transition: { type: 'spring', bounce: 0 },
    },
  },
  initial: 'start',
  whileInView: 'end',
  viewport: { once: true, amount: 0.1, margin: '200px' },
}

// 300 value added randomly to avoid safari bug where the carousel would start from
// the left during animation
const INITIAL_WIDTH = 300
const DESKTOP_MAX_CARD_WIDTH = 708
const MAX_CONTENT_WIDTH = 1063 // div with `content` className max width
export const SayHelloSection = forwardRef<HTMLDivElement>((_, ref) => {
  const [width, setWidth] = useState<number | undefined>(INITIAL_WIDTH)
  const [currentSlide, setCurrentSlide] = useState(FIRST_ITEM_INDEX)
  const [isAutoPlayActive, setIsAutoPlayActive] = useState(false)

  const containerRef = useRef(null)
  const gridRef = useRef(null)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const { windowWidth } = useWindowSize()

  const onNextClick = useCallback(() => {
    setCurrentSlide(prevItem => {
      if (prevItem === NUMBER_OF_ITEM - 1) {
        return FIRST_ITEM_INDEX
      }
      return prevItem + 1
    })
  }, [])

  const onPreviousClick = useCallback(() => {
    setCurrentSlide(prevItem => {
      if (prevItem === FIRST_ITEM_INDEX) {
        return NUMBER_OF_ITEM - 1
      }
      return prevItem - 1
    })
  }, [])

  const onChange = useCallback((currentIndex: number) => {
    setCurrentSlide(currentIndex)
  }, [])

  useEffect(() => {
    if (containerRef.current && gridRef.current && buttonRef.current && windowWidth) {
      // This calculation is taking into account the window width and removing the lateral paddings
      // (- 2 * leftPadding)
      //  So windowWidth - 2 * wrapperLeftPadding
      const cardWidth =
        parseInt(window.getComputedStyle(containerRef.current).getPropertyValue('width'), 10) -
        2 *
          parseInt(
            window.getComputedStyle(containerRef.current).getPropertyValue('padding-left'),
            10
          ) -
        WIDTH_ADJUSTMENT

      if (windowWidth < WEBSITE_BREAKPOINTS.MEDIUM) {
        // The content on the section has a max width, so it's making sure the
        // width isn't bigger than that
        setWidth(cardWidth > MAX_CONTENT_WIDTH ? MAX_CONTENT_WIDTH : cardWidth)
        return
      }

      if (windowWidth < WEBSITE_BREAKPOINTS.LARGE) {
        // for lg viewport, it takes into account the arrow buttons size and the gap between
        // buttons and card. Maximum card size should be fixed though, so it makes sure it's
        // not going above that
        const lgWidth =
          cardWidth -
          2 * buttonRef.current.offsetWidth -
          2 * parseInt(window.getComputedStyle(gridRef.current).getPropertyValue('column-gap'), 10)
        setWidth(lgWidth > DESKTOP_MAX_CARD_WIDTH ? DESKTOP_MAX_CARD_WIDTH : lgWidth)
        return
      }

      setWidth(DESKTOP_MAX_CARD_WIDTH)
    }
  }, [windowWidth])

  const onAnimationComplete = useCallback(() => {
    setIsAutoPlayActive(true)
  }, [])

  return (
    <div className={styles['container']} ref={ref}>
      <section className={styles['wrapper']} ref={containerRef}>
        <LazyMotion features={domAnimation} strict>
          <m.div
            className={styles['content']}
            {...ANIMATION_PROPS}
            onAnimationComplete={onAnimationComplete}
          >
            <h2 className={styles['title']}>Say “hello” to a new adventure</h2>
            <p className={styles['description']}>
              Work with big companies and take the next step of your career.
            </p>
            <div className={styles['carousel-container']} ref={gridRef}>
              <Carousel
                autoPlay={isAutoPlayActive}
                selectedItem={currentSlide}
                showArrows={false}
                showStatus={false}
                ariaLabel="Case studies"
                showIndicators={false}
                showThumbs={false}
                onChange={onChange}
                className={styles['carousel']}
                infiniteLoop={true}
                width={width}
                interval={10000}
              >
                {STEP_ITEMS.map(item => (
                  <div key={item.id} className={styles['card']}>
                    <a
                      className={styles['link']}
                      href={item.externalHref}
                      target="_blank"
                      rel="noreferrer"
                      aria-label={`Access ${item.id} homepage`}
                    >
                      <item.icon className={styles['icon']} />
                    </a>
                    <p className={styles['text']}>{item.text}</p>
                    <div className={styles['author']}>
                      <Avatar
                        name={item.name}
                        image={item.avatar}
                        size="xl"
                        className={styles['author-avatar']}
                      />
                      <p className={styles['name']}>{item.name}</p>
                      <p className={styles['position']}>{item.position}</p>
                    </div>
                    {item.href && (
                      <Link
                        size="large"
                        href={item.href}
                        prefetch={false}
                        className={styles['read-more']}
                      >
                        Read more
                      </Link>
                    )}
                  </div>
                ))}
              </Carousel>
              <CircleButton
                onClick={onPreviousClick}
                icon={ChevronLeftIcon}
                label="Previous item"
                variant="primary"
                className={styles['left']}
                ref={buttonRef}
              />
              <CircleButton
                onClick={onNextClick}
                icon={ChevronRightIcon}
                label="Next item"
                variant="primary"
                className={styles['right']}
              />
              <div className={styles['link-container']}>
                <Button
                  variant="secondary"
                  size="large"
                  href="/case-studies"
                  className={styles['link']}
                  prefetch={false}
                >
                  See all case studies
                </Button>
              </div>
            </div>
          </m.div>
        </LazyMotion>
      </section>
    </div>
  )
})

export default SayHelloSection
